/**
 * Crossfade controller for the Slideshow section (Slide animation = "Fade").
 *
 * Horizon's <slideshow-component> (assets/slideshow.js) already no-ops its
 * scroll-sync (#handleScroll) and drag lifecycle (#handleMouseDown) when the
 * element carries the `fade` attribute, and dispatches SlideshowSelectEvent
 * ("slideshow:select", bubbles) on every arrow / dot / autoplay / next() /
 * previous() change. It does NOT toggle the crossfade marker or add a touch
 * swipe (native scroll-swipe is gone once slides are stacked). The PDP gallery
 * (media-gallery.js) and card gallery (product-card.js) do that for their own
 * faded slideshows; this module does it for the Slideshow *section*, scoped to
 * `.slideshow-section` so those other controllers are never double-driven.
 *
 * FORK NOTE: when the blog-slideshow section is forked, give its wrapper the
 * `.slideshow-section` class (or extend SCOPE) so it inherits this behavior.
 */
import { SlideshowSelectEvent } from "@theme/events";

const SCOPE = ".slideshow-section";
const SWIPE_THRESHOLD_PX = 40;

/** @param {Element} slideshow */
const enhance = (slideshow) => {
  const el = /** @type {HTMLElement} */ (slideshow);
  if (el.dataset.fadeEnhanced) return;
  el.dataset.fadeEnhanced = "true";

  const isFade = () => slideshow.hasAttribute("fade");

  const markActive = () => {
    if (!isFade()) return;
    const slides = Array.from(slideshow.querySelectorAll("slideshow-slide"));
    if (!slides.length) return;
    const index = /** @type {any} */ (slideshow).current ?? 0;
    const active = slides[index] ?? slides[0];
    for (const slide of slides) slide.toggleAttribute("data-fade-active", slide === active);
  };

  slideshow.addEventListener(SlideshowSelectEvent.eventName, markActive);

  /** @type {{ x: number, y: number } | null} */
  let swipeStart = null;
  let swiped = false;

  slideshow.addEventListener("pointerdown", (event) => {
    const e = /** @type {PointerEvent} */ (event);
    if (!isFade() || e.pointerType !== "touch") return;
    swipeStart = { x: e.clientX, y: e.clientY };
    swiped = false;
  });

  slideshow.addEventListener("pointerup", (event) => {
    const e = /** @type {PointerEvent} */ (event);
    if (!isFade() || e.pointerType !== "touch" || !swipeStart) return;
    const dx = e.clientX - swipeStart.x;
    const dy = e.clientY - swipeStart.y;
    swipeStart = null;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      swiped = true;
      if (dx < 0) /** @type {any} */ (slideshow).next();
      else /** @type {any} */ (slideshow).previous();
    }
  });

  // Swallow the click that trails a swipe so it doesn't fire a link/tap in the slide.
  slideshow.addEventListener(
    "click",
    (event) => {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    },
    { capture: true }
  );

  requestAnimationFrame(markActive);
};

/** @param {ParentNode | Element} root */
const scan = (root) => {
  if (root instanceof Element && root.matches("slideshow-component[fade]") && root.closest(SCOPE)) {
    enhance(root);
  }
  root.querySelectorAll?.("slideshow-component[fade]").forEach((el) => {
    if (el.closest(SCOPE)) enhance(el);
  });
};

scan(document);

// Re-scan on theme-editor section re-renders / dynamically inserted slideshows.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof Element) scan(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
